import { useState, useEffect } from 'react';

export type OperationMode = 'normal' | 'event';

interface PrinterSettings {
  ip: string;
  port: string;
  type: 'network' | 'usb' | 'windows';
  model: 'star' | 'epson';
  profile: string;
}

interface OperationModeConfig {
  mode: OperationMode;
  printerSettings: PrinterSettings;
}

const NORMAL_MODE_CONFIG: OperationModeConfig = {
  mode: 'normal',
  printerSettings: {
    ip: '192.168.0.188',
    port: '9100',
    type: 'network',
    model: 'star',
    profile: '店舗'
  }
};

const EVENT_MODE_CONFIG: OperationModeConfig = {
  mode: 'event',
  printerSettings: {
    ip: '192.168.1.100', // 出張先のプリンターIP（実際の値は後で設定画面から変更可能）
    port: '9100',
    type: 'network',
    model: 'star',
    profile: '出張販売'
  }
};

export const useOperationMode = () => {
  const [mode, setMode] = useState<OperationMode>('normal');

  useEffect(() => {
    // 初期化時に localStorage から読み込み
    const savedMode = localStorage.getItem('operationMode') as OperationMode;
    if (savedMode === 'event' || savedMode === 'normal') {
      setMode(savedMode);
    }
  }, []);

  const toggleMode = () => {
    const newMode: OperationMode = mode === 'event' ? 'normal' : 'event';
    const config = newMode === 'event' ? EVENT_MODE_CONFIG : NORMAL_MODE_CONFIG;

    // 操作モードを保存
    localStorage.setItem('operationMode', newMode);

    // プリンター設定も自動変更
    localStorage.setItem('printerSettings', JSON.stringify(config.printerSettings));

    // 状態更新
    setMode(newMode);

    // グローバルイベント発行（他のコンポーネントに通知）
    window.dispatchEvent(new CustomEvent('operationModeChanged', {
      detail: { mode: newMode, printerSettings: config.printerSettings }
    }));

    console.log(`✅ 操作モード切り替え: ${mode} → ${newMode}`);
    console.log(`📍 プリンターIP: ${config.printerSettings.ip} (${config.printerSettings.profile})`);
  };

  const setEventModeIP = (ip: string) => {
    // 出張モードのプリンターIPを更新
    EVENT_MODE_CONFIG.printerSettings.ip = ip;

    // 現在出張モードの場合は即座に反映
    if (mode === 'event') {
      localStorage.setItem('printerSettings', JSON.stringify(EVENT_MODE_CONFIG.printerSettings));
    }
  };

  return {
    mode,
    toggleMode,
    setEventModeIP,
    isEventMode: mode === 'event',
    isNormalMode: mode === 'normal'
  };
};

// 操作モードを取得するヘルパー関数（React外でも使用可能）
export const getOperationMode = (): OperationMode => {
  const savedMode = localStorage.getItem('operationMode') as OperationMode;
  return savedMode === 'event' ? 'event' : 'normal';
};
